import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { SharedSessionAuthService } from '../../services/shared-session-auth.service';

@Component({
  selector: 'app-join',
  templateUrl: './join.component.html',
  styleUrls: ['./join.component.scss'],
})
export class JoinComponent implements OnInit, OnDestroy {
  private static readonly MIN_SPLASH_DURATION_MS = 2000;
  private static readonly SPLASH_FADE_MS = 450;
  // MIC-320: a scheduled session's /join returns 409 until the host activates it.
  // We sit in the lobby and re-poll on this cadence; it flips to 200 the moment
  // the host presses Start.
  private static readonly LOBBY_POLL_MS = 10_000;
  code = '';
  submitting = false;
  autoJoining = false;
  splashLeaving = false;
  fromQr = false;
  qrFailed = false;
  manualOverride = false;
  sessionEndedByHost = false;
  endedDeviceUuid: string | null = null;
  splashDeviceUuid: string | null = null;
  errorMessage = '';
  scheduledStartsAt: string | null = null;
  inLobby = false;
  private scheduledTimer: ReturnType<typeof setTimeout> | null = null;
  private lobbyTimer: ReturnType<typeof setInterval> | null = null;
  private navTimer: ReturnType<typeof setTimeout> | null = null;
  private autoJoinStartedAt = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: SharedSessionAuthService
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.get('ended') === '1') {
      this.sessionEndedByHost = true;
      this.endedDeviceUuid = this.route.snapshot.queryParamMap.get('device');
      return;
    }
    const code = (this.route.snapshot.paramMap.get('code') ?? '').replace(/\D/g, '');
    // Only auto-join on a well-formed 6-digit code. A malformed param must not
    // strand the user on the connecting splash (submit() would no-op on a
    // wrong-length code while autoJoining stays true), so fall through to the
    // manual entry form instead.
    if (/^\d{6}$/.test(code)) {
      this.code = code;
      this.fromQr = true;
      this.autoJoining = true;
      this.autoJoinStartedAt = Date.now();
      this.submit();
    }
  }

  ngOnDestroy(): void {
    if (this.scheduledTimer !== null) clearTimeout(this.scheduledTimer);
    if (this.navTimer !== null) clearTimeout(this.navTimer);
    this.stopLobbyPolling();
  }

  onCodeChange(value: string): void {
    this.code = value;
  }

  submit(): void {
    if (this.code.length !== 6) return;
    this.submitting = true;
    this.errorMessage = '';
    this.qrFailed = false;
    this.auth.join(this.code).subscribe({
      next: (res) => {
        this.submitting = false;
        this.splashDeviceUuid = res.device_uuid;
        if (res.status === 'scheduled') {
          this.enterScheduled(res);
          return;
        }
        this.navigateToSpeech(res.device_uuid);
      },
      error: (err) => {
        this.submitting = false;
        // MIC-320: 409 means the code is real but the session is scheduled and
        // hasn't been started yet. That's a lobby, not a failure.
        if (err?.status === 409) {
          this.enterLobby();
          return;
        }
        this.autoJoining = false;
        if (this.fromQr && !this.manualOverride) {
          this.qrFailed = true;
          this.errorMessage = '';
        } else if (err.status === 500) {
          this.errorMessage = 'Something went wrong. Please try again.';
        } else {
          this.errorMessage = "Couldn't join. Check the code or ask the host.";
        }
      },
    });
  }

  private enterLobby(): void {
    this.autoJoining = false;
    this.splashLeaving = false;
    this.qrFailed = false;
    this.errorMessage = '';
    this.inLobby = true;
    this.startLobbyPolling();
  }

  private startLobbyPolling(): void {
    if (this.lobbyTimer !== null) return;
    this.lobbyTimer = setInterval(() => this.pollJoin(), JoinComponent.LOBBY_POLL_MS);
  }

  private stopLobbyPolling(): void {
    if (this.lobbyTimer !== null) {
      clearInterval(this.lobbyTimer);
      this.lobbyTimer = null;
    }
  }

  private pollJoin(): void {
    this.auth.join(this.code).subscribe({
      next: (res) => {
        this.stopLobbyPolling();
        this.inLobby = false;
        this.splashDeviceUuid = res.device_uuid;
        if (res.status === 'scheduled') {
          this.enterScheduled(res);
          return;
        }
        this.navigateToSpeech(res.device_uuid);
      },
      error: (err) => {
        // Still scheduled (409), rate-limited (429) or a transient server hiccup
        // (500): stay in the lobby and let the next tick retry.
        if (err?.status === 409 || err?.status === 429 || err?.status === 500) return;
        // Anything terminal (400 invalid/expired, 404): the code no longer works.
        this.stopLobbyPolling();
        this.inLobby = false;
        if (this.fromQr && !this.manualOverride) {
          this.qrFailed = true;
        } else {
          this.errorMessage = "Couldn't join. Check the code or ask the host.";
        }
      },
    });
  }

  retryQrJoin(): void {
    this.qrFailed = false;
    this.autoJoining = true;
    this.submit();
  }

  enterCodeManually(): void {
    this.qrFailed = false;
    this.manualOverride = true;
    this.fromQr = false;
    this.code = '';
  }

  private navigateToSpeech(deviceUuid: string): void {
    const elapsed = this.autoJoinStartedAt > 0 ? Date.now() - this.autoJoinStartedAt : 0;
    const remaining = Math.max(0, JoinComponent.MIN_SPLASH_DURATION_MS - elapsed);
    const navigate = () => {
      this.router.navigate(['/speech', deviceUuid]).then(
        (ok) => {
          if (!ok) this.handleNavigationFailure();
        },
        () => this.handleNavigationFailure()
      );
    };
    const beginFadeOut = () => {
      this.splashLeaving = true;
      this.navTimer = setTimeout(navigate, JoinComponent.SPLASH_FADE_MS);
    };
    if (remaining > 0) {
      this.navTimer = setTimeout(beginFadeOut, remaining);
    } else {
      beginFadeOut();
    }
  }

  private handleNavigationFailure(): void {
    this.autoJoining = false;
    if (this.fromQr && !this.manualOverride) {
      this.qrFailed = true;
      this.errorMessage = '';
    } else {
      this.errorMessage = "We couldn't open the captions page. Please try again.";
    }
  }

  /**
   * Handle a scheduled session. If it hasn't started yet, leave the connecting
   * splash and show the "starts soon" card while we wait for the start time
   * (mirroring the lobby transition). If the start time has already passed, go
   * straight into the captions.
   */
  private enterScheduled(res: { device_uuid: string; starts_at?: string | null }): void {
    const startsAt = res.starts_at ?? null;
    const delay = startsAt ? new Date(startsAt).getTime() - Date.now() : 0;
    if (delay <= 0) {
      this.navigateToSpeech(res.device_uuid);
      return;
    }
    this.autoJoining = false;
    this.splashLeaving = false;
    this.scheduledStartsAt = startsAt;
    this.scheduledTimer = setTimeout(() => this.navigateToSpeech(res.device_uuid), delay);
  }
}
